const colorPickerRegistry = new WeakMap();
const colorPickerInstances = new Set();
const presetColors = createPresetColors();

export function enhanceChartColorPickers(root = document) {
  return [...root.querySelectorAll('input[type="color"][data-chart-color-picker]')]
    .map((input) => enhanceChartColorPicker(input));
}

export function enhanceChartColorPicker(input, {
  showOpacity = true,
  closeOnCommit = false
} = {}) {
  if (!(input instanceof HTMLInputElement) || input.type !== "color") {
    throw new TypeError("A chart color picker requires a color input.");
  }

  const existing = colorPickerRegistry.get(input);
  if (existing) return existing;

  const field = normalizeFieldContainer(input);
  const label = resolvePickerLabel(input, field);
  const picker = document.createElement("div");
  const trigger = document.createElement("button");
  const swatch = document.createElement("span");
  const popover = document.createElement("div");
  const saturation = document.createElement("div");
  const saturationThumb = document.createElement("span");
  const hue = document.createElement("input");
  const hexInput = document.createElement("input");
  const presets = document.createElement("div");
  const presetView = document.createElement("div");
  const advancedView = document.createElement("div");
  const currentValue = document.createElement("output");
  const customButton = document.createElement("button");
  const backButton = document.createElement("button");
  const opacityField = document.createElement("label");
  const opacityLabel = document.createElement("span");
  const opacityValue = document.createElement("output");
  const opacityInput = document.createElement("input");

  picker.className = "chart-color-picker";
  picker.dataset.chartColorPickerRoot = "";
  picker.style.setProperty("--chart-picker-hue", "0");

  trigger.type = "button";
  trigger.className = "chart-color-trigger";
  trigger.setAttribute("aria-label", label);
  trigger.setAttribute("aria-haspopup", "dialog");
  trigger.setAttribute("aria-expanded", "false");
  trigger.title = label;

  swatch.className = "chart-color-trigger-swatch";
  trigger.append(swatch, createChevron());

  popover.className = "chart-color-popover";
  popover.dataset.chartColorPickerPopover = "";
  popover.setAttribute("role", "dialog");
  popover.setAttribute("aria-label", `${label} color picker`);
  popover.hidden = true;
  popover.append(createHeader(label));

  presetView.className = "chart-color-preset-view";
  presets.className = "chart-color-presets";
  presets.setAttribute("aria-label", `${label} color presets`);
  presetView.append(presets);

  const presetFooter = document.createElement("div");
  presetFooter.className = "chart-color-preset-footer";
  currentValue.className = "chart-color-current-value";
  currentValue.setAttribute("aria-label", `${label} current value`);
  customButton.type = "button";
  customButton.className = "chart-color-custom-button";
  customButton.setAttribute("aria-label", `Open ${label.toLowerCase()} saturation and hue controls`);
  customButton.setAttribute("aria-expanded", "false");
  customButton.title = "Custom color";
  customButton.textContent = "+";
  presetFooter.append(currentValue, customButton);
  presetView.append(presetFooter);
  popover.append(presetView);

  advancedView.className = "chart-color-advanced-view";
  advancedView.hidden = true;
  const advancedToolbar = document.createElement("div");
  advancedToolbar.className = "chart-color-advanced-toolbar";
  backButton.type = "button";
  backButton.className = "chart-color-back-button";
  backButton.setAttribute("aria-label", `Back to ${label.toLowerCase()} presets`);
  backButton.textContent = "← Presets";
  const advancedLabel = document.createElement("span");
  advancedLabel.textContent = "Custom";
  advancedToolbar.append(backButton, advancedLabel);
  advancedView.append(advancedToolbar);

  saturation.className = "chart-color-saturation";
  saturation.tabIndex = 0;
  saturation.setAttribute("aria-label", `${label} saturation and brightness`);
  saturation.setAttribute(
    "aria-description",
    "Use arrow keys to adjust saturation and brightness. Hold Shift for larger steps."
  );
  saturationThumb.className = "chart-color-saturation-thumb";
  saturation.append(saturationThumb);
  advancedView.append(saturation);

  const hueField = document.createElement("label");
  hueField.className = "chart-color-hue-field";
  const hueLabel = document.createElement("span");
  hueLabel.textContent = "Hue";
  hue.type = "range";
  hue.min = "0";
  hue.max = "360";
  hue.step = "1";
  hue.className = "chart-color-hue";
  hue.setAttribute("aria-label", `${label} hue`);
  hueField.append(hueLabel, hue);
  advancedView.append(hueField);

  const hexField = document.createElement("label");
  hexField.className = "chart-color-hex-field";
  const hexLabel = document.createElement("span");
  hexLabel.textContent = "Hex";
  hexInput.type = "text";
  hexInput.className = "chart-color-hex";
  hexInput.inputMode = "text";
  hexInput.maxLength = 7;
  hexInput.spellcheck = false;
  hexInput.setAttribute("aria-label", `${label} hex value`);
  hexField.append(hexLabel, hexInput);
  advancedView.append(hexField);
  popover.append(advancedView);

  opacityField.className = "chart-color-opacity-field";
  opacityLabel.textContent = "Opacity";
  opacityValue.className = "chart-color-opacity-value";
  opacityInput.type = "range";
  opacityInput.min = "0";
  opacityInput.max = "100";
  opacityInput.step = "1";
  opacityInput.className = "chart-color-opacity";
  opacityInput.setAttribute("aria-label", `${label} opacity`);
  const opacityHeading = document.createElement("span");
  opacityHeading.className = "chart-color-opacity-label";
  opacityHeading.append(opacityLabel, opacityValue);
  opacityField.append(opacityHeading, opacityInput);
  if (showOpacity) popover.append(opacityField);

  input.hidden = true;
  input.tabIndex = -1;
  input.setAttribute("aria-hidden", "true");
  input.insertAdjacentElement("afterend", picker);
  picker.append(trigger);
  document.body.append(popover);

  let open = false;
  let color = normalizeHex(input.value) || "#000000";
  let hsv = hexToHsv(color);
  let opacity = normalizeOpacity(input.dataset.chartOpacity);
  let saturationDragging = false;
  let presetButtons = [];
  let advanced = false;

  function ensurePresetButtons() {
    if (presetButtons.length > 0) return;
    presetButtons = presetColors.map((presetColor) => {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "chart-color-preset";
      button.dataset.color = presetColor;
      button.style.backgroundColor = presetColor;
      button.setAttribute("aria-label", `Set ${label.toLowerCase()} to ${presetColor}`);
      button.title = presetColor;
      presets.append(button);
      return button;
    });
  }

  function sync(nextColor = input.value) {
    const normalized = normalizeHex(nextColor);
    if (!normalized) return;
    color = normalized;
    hsv = hexToHsv(color);
    picker.style.setProperty("--chart-picker-hue", String(Math.round(hsv.h)));
    swatch.style.backgroundColor = hexToRgba(color, opacity);
    hexInput.value = color;
    hue.value = String(Math.round(hsv.h));
    saturationThumb.style.left = `${hsv.s * 100}%`;
    saturationThumb.style.top = `${(1 - hsv.v) * 100}%`;
    saturation.setAttribute(
      "aria-valuetext",
      `${Math.round(hsv.s * 100)}% saturation, ${Math.round(hsv.v * 100)}% brightness`
    );
    trigger.title = `${label}: ${color}`;
    currentValue.value = color;
    currentValue.textContent = color;
    for (const button of presetButtons) {
      button.classList.toggle("is-selected", button.dataset.color === color);
    }
    syncOpacity();
  }

  function syncOpacity(nextOpacity = input.dataset.chartOpacity) {
    opacity = normalizeOpacity(nextOpacity);
    input.dataset.chartOpacity = String(opacity);
    opacityInput.value = String(opacity);
    opacityValue.value = `${opacity}%`;
    opacityValue.textContent = `${opacity}%`;
    swatch.style.backgroundColor = hexToRgba(color, opacity);
  }

  function emitColor(nextColor, { commit = false } = {}) {
    const normalized = normalizeHex(nextColor);
    if (!normalized) return;
    input.value = normalized.toLowerCase();
    sync(normalized);
    input.dispatchEvent(new Event("input", { bubbles: true }));
    if (commit) {
      input.dispatchEvent(new Event("change", { bubbles: true }));
      if (closeOnCommit) close({ restoreFocus: true });
    }
  }

  function emitHsv({ commit = false } = {}) {
    emitColor(hsvToHex(hsv), { commit });
  }

  function emitOpacity({ commit = false } = {}) {
    syncOpacity(opacityInput.value);
    input.dispatchEvent(new CustomEvent("chart-color-opacity", {
      bubbles: true,
      detail: { opacity, commit }
    }));
    if (commit && closeOnCommit) close({ restoreFocus: true });
  }

  function showAdvanced(nextAdvanced, { focus = true } = {}) {
    advanced = nextAdvanced;
    presetView.hidden = advanced;
    advancedView.hidden = !advanced;
    customButton.setAttribute("aria-expanded", String(advanced));
    if (open) positionPopover();
    if (!focus) return;
    if (advanced) saturation.focus();
    else customButton.focus();
  }

  function close({ restoreFocus = false } = {}) {
    if (!open) return;
    open = false;
    saturationDragging = false;
    showAdvanced(false, { focus: false });
    popover.hidden = true;
    trigger.setAttribute("aria-expanded", "false");
    picker.classList.remove("is-open");
    if (restoreFocus) trigger.focus();
  }

  function openPicker() {
    for (const other of colorPickerInstances) {
      if (other.input !== input) other.close();
    }
    open = true;
    popover.hidden = false;
    trigger.setAttribute("aria-expanded", "true");
    picker.classList.add("is-open");
    ensurePresetButtons();
    showAdvanced(false, { focus: false });
    sync();
    positionPopover();
    (presetButtons.find((button) => button.classList.contains("is-selected"))
      || presetButtons[0]
      || customButton).focus();
  }

  function positionPopover() {
    const boundary = picker.closest(".chart-settings-window, .chart-indicator-manager")
      ?.getBoundingClientRect()
      || { top: 0, right: window.innerWidth, bottom: window.innerHeight, left: 0, width: window.innerWidth };
    const inset = 8;
    const width = Math.min(252, window.innerWidth - 24, Math.max(180, boundary.width - inset * 2));
    popover.style.width = `${width}px`;
    popover.style.maxHeight = `${Math.max(
      180,
      Math.min(window.innerHeight - 24, boundary.bottom - boundary.top - inset * 2)
    )}px`;
    const triggerBounds = trigger.getBoundingClientRect();
    const popoverBounds = popover.getBoundingClientRect();
    const roomBelow = boundary.bottom - triggerBounds.bottom;
    const roomAbove = triggerBounds.top - boundary.top;
    const preferredTop = popoverBounds.height <= roomBelow || roomBelow >= roomAbove
      ? triggerBounds.bottom + 6
      : triggerBounds.top - popoverBounds.height - 6;
    const top = clamp(
      preferredTop,
      Math.max(inset, boundary.top + inset),
      Math.min(window.innerHeight - popoverBounds.height - inset, boundary.bottom - popoverBounds.height - inset)
    );
    const left = clamp(
      triggerBounds.right - width,
      Math.max(inset, boundary.left + inset),
      Math.min(window.innerWidth - width - inset, boundary.right - width - inset)
    );
    popover.style.top = `${top}px`;
    popover.style.left = `${left}px`;
  }

  function updateSaturationFromPointer(event, commit = false) {
    const bounds = saturation.getBoundingClientRect();
    hsv.s = clamp((event.clientX - bounds.left) / Math.max(1, bounds.width), 0, 1);
    hsv.v = 1 - clamp((event.clientY - bounds.top) / Math.max(1, bounds.height), 0, 1);
    emitHsv({ commit });
  }

  function handleSaturationPointerDown(event) {
    event.preventDefault();
    saturationDragging = true;
    saturation.setPointerCapture(event.pointerId);
    updateSaturationFromPointer(event);
  }

  function handleSaturationPointerMove(event) {
    if (!saturationDragging) return;
    updateSaturationFromPointer(event);
  }

  function handleSaturationPointerUp(event) {
    if (!saturationDragging) return;
    saturationDragging = false;
    if (saturation.hasPointerCapture(event.pointerId)) saturation.releasePointerCapture(event.pointerId);
    updateSaturationFromPointer(event, true);
  }

  function handleSaturationKeydown(event) {
    if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)) return;
    event.preventDefault();
    event.stopPropagation();
    const step = event.shiftKey ? 0.1 : 0.01;
    if (event.key === "ArrowLeft") hsv.s = clamp(hsv.s - step, 0, 1);
    if (event.key === "ArrowRight") hsv.s = clamp(hsv.s + step, 0, 1);
    if (event.key === "ArrowUp") hsv.v = clamp(hsv.v + step, 0, 1);
    if (event.key === "ArrowDown") hsv.v = clamp(hsv.v - step, 0, 1);
    emitHsv({ commit: true });
  }

  function handleHexInput({ commit = false } = {}) {
    const normalized = normalizeHex(hexInput.value);
    if (normalized) {
      hexInput.setAttribute("aria-invalid", "false");
      emitColor(normalized, { commit });
    } else {
      hexInput.setAttribute("aria-invalid", "true");
    }
  }

  function handlePopoverKeydown(event) {
    if (event.key !== "Escape") return;
    event.preventDefault();
    event.stopPropagation();
    close({ restoreFocus: true });
  }

  function handleDocumentPointerDown(event) {
    if (
      open
      && event.target instanceof Node
      && !picker.contains(event.target)
      && !popover.contains(event.target)
    ) close();
  }

  function handleWindowResize() {
    if (open) positionPopover();
  }

  trigger.addEventListener("click", () => open ? close() : openPicker());
  trigger.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && open) {
      event.preventDefault();
      event.stopPropagation();
      close({ restoreFocus: true });
    }
  });
  popover.addEventListener("keydown", handlePopoverKeydown);
  saturation.addEventListener("pointerdown", handleSaturationPointerDown);
  saturation.addEventListener("pointermove", handleSaturationPointerMove);
  saturation.addEventListener("pointerup", handleSaturationPointerUp);
  saturation.addEventListener("pointercancel", handleSaturationPointerUp);
  saturation.addEventListener("keydown", handleSaturationKeydown);
  hue.addEventListener("input", () => {
    hsv.h = Number.parseFloat(hue.value);
    emitHsv();
  });
  hue.addEventListener("change", () => {
    hsv.h = Number.parseFloat(hue.value);
    emitHsv({ commit: true });
  });
  opacityInput.addEventListener("input", () => emitOpacity());
  opacityInput.addEventListener("change", () => emitOpacity({ commit: true }));
  hexInput.addEventListener("input", () => handleHexInput());
  hexInput.addEventListener("change", () => {
    handleHexInput({ commit: true });
    if (hexInput.getAttribute("aria-invalid") === "true") sync();
  });
  hexInput.addEventListener("keydown", (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleHexInput({ commit: true });
    }
  });
  customButton.addEventListener("click", () => showAdvanced(true));
  backButton.addEventListener("click", () => showAdvanced(false));
  presets.addEventListener("click", (event) => {
    const button = event.target instanceof Element
      ? event.target.closest(".chart-color-preset")
      : null;
    if (!(button instanceof HTMLButtonElement) || !presets.contains(button)) return;
    emitColor(button.dataset.color, { commit: true });
  });
  input.addEventListener("change", () => sync());
  document.addEventListener("pointerdown", handleDocumentPointerDown);
  window.addEventListener("resize", handleWindowResize);

  const instance = {
    input,
    trigger,
    popover,
    sync,
    syncOpacity,
    close,
    isOpen: () => open,
    destroy() {
      close();
      document.removeEventListener("pointerdown", handleDocumentPointerDown);
      window.removeEventListener("resize", handleWindowResize);
      input.hidden = false;
      input.tabIndex = 0;
      input.removeAttribute("aria-hidden");
      popover.remove();
      picker.remove();
      colorPickerRegistry.delete(input);
      colorPickerInstances.delete(instance);
    }
  };

  sync();
  colorPickerRegistry.set(input, instance);
  colorPickerInstances.add(instance);
  return instance;
}

export function syncChartColorPicker(input) {
  colorPickerRegistry.get(input)?.sync();
}

function normalizeFieldContainer(input) {
  const parent = input.parentElement;
  if (!(parent instanceof HTMLLabelElement) || !parent.classList.contains("chart-setting-color")) {
    return parent;
  }

  const field = document.createElement("div");
  for (const attribute of parent.attributes) {
    field.setAttribute(attribute.name, attribute.value);
  }
  while (parent.firstChild) field.append(parent.firstChild);
  parent.replaceWith(field);
  return field;
}

function resolvePickerLabel(input, field) {
  const labelledBy = input.getAttribute("aria-labelledby");
  if (labelledBy) {
    const text = document.getElementById(labelledBy)?.textContent?.trim();
    if (text) return text;
  }
  const ariaLabel = input.getAttribute("aria-label")?.trim();
  if (ariaLabel) return ariaLabel;
  const text = field?.querySelector(":scope > span")?.textContent?.trim();
  return text || "Color";
}

function createHeader(label) {
  const header = document.createElement("div");
  const title = document.createElement("strong");
  const hint = document.createElement("span");
  header.className = "chart-color-popover-head";
  title.textContent = label;
  hint.textContent = "Live preview";
  header.append(title, hint);
  return header;
}

function createChevron() {
  const namespace = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(namespace, "svg");
  const path = document.createElementNS(namespace, "path");
  svg.classList.add("chart-color-trigger-chevron");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("aria-hidden", "true");
  path.setAttribute("d", "m6 9 6 6 6-6");
  svg.append(path);
  return svg;
}

function createPresetColors() {
  const neutralValues = [1, 0.91, 0.82, 0.72, 0.62, 0.52, 0.42, 0.32, 0.22, 0.1];
  const hueRows = [0, 28, 48, 88, 140, 170, 195, 220, 270];
  const variants = [
    { s: 0.14, v: 0.98 },
    { s: 0.28, v: 0.96 },
    { s: 0.42, v: 0.94 },
    { s: 0.56, v: 0.92 },
    { s: 0.7, v: 0.9 },
    { s: 0.84, v: 0.88 },
    { s: 0.94, v: 0.78 },
    { s: 0.96, v: 0.66 },
    { s: 0.94, v: 0.52 },
    { s: 0.86, v: 0.38 }
  ];
  return [
    ...neutralValues.map((value) => hsvToHex({ h: 0, s: 0, v: value })),
    ...hueRows.flatMap((hueValue) => variants.map(({ s, v }) => (
      hsvToHex({ h: hueValue, s, v })
    )))
  ];
}

function normalizeHex(value) {
  const candidate = String(value || "").trim().toUpperCase();
  if (/^#[0-9A-F]{6}$/.test(candidate)) return candidate;
  if (/^[0-9A-F]{6}$/.test(candidate)) return `#${candidate}`;
  if (/^#[0-9A-F]{3}$/.test(candidate)) {
    return `#${candidate.slice(1).split("").map((character) => character.repeat(2)).join("")}`;
  }
  return null;
}

function normalizeOpacity(value) {
  const parsed = Number.parseFloat(value);
  return Math.round(clamp(Number.isFinite(parsed) ? parsed : 100, 0, 100));
}

function hexToRgba(hex, opacity) {
  const red = Number.parseInt(hex.slice(1, 3), 16);
  const green = Number.parseInt(hex.slice(3, 5), 16);
  const blue = Number.parseInt(hex.slice(5, 7), 16);
  return `rgba(${red}, ${green}, ${blue}, ${opacity / 100})`;
}

function hexToHsv(hex) {
  const red = Number.parseInt(hex.slice(1, 3), 16) / 255;
  const green = Number.parseInt(hex.slice(3, 5), 16) / 255;
  const blue = Number.parseInt(hex.slice(5, 7), 16) / 255;
  const maximum = Math.max(red, green, blue);
  const minimum = Math.min(red, green, blue);
  const delta = maximum - minimum;
  let hue = 0;

  if (delta > 0) {
    if (maximum === red) hue = 60 * (((green - blue) / delta) % 6);
    else if (maximum === green) hue = 60 * ((blue - red) / delta + 2);
    else hue = 60 * ((red - green) / delta + 4);
  }

  if (hue < 0) hue += 360;
  return {
    h: hue,
    s: maximum === 0 ? 0 : delta / maximum,
    v: maximum
  };
}

function hsvToHex({ h, s, v }) {
  const chroma = v * s;
  const segment = (h / 60) % 6;
  const x = chroma * (1 - Math.abs((segment % 2) - 1));
  const offset = v - chroma;
  let red = 0;
  let green = 0;
  let blue = 0;

  if (segment < 1) [red, green] = [chroma, x];
  else if (segment < 2) [red, green] = [x, chroma];
  else if (segment < 3) [green, blue] = [chroma, x];
  else if (segment < 4) [green, blue] = [x, chroma];
  else if (segment < 5) [red, blue] = [x, chroma];
  else [red, blue] = [chroma, x];

  return `#${[red, green, blue]
    .map((channel) => Math.round((channel + offset) * 255).toString(16).padStart(2, "0"))
    .join("")
    .toUpperCase()}`;
}

function clamp(value, minimum, maximum) {
  return Math.min(maximum, Math.max(minimum, value));
}
